/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useTenant } from "hooks/settings";
import { Loaded, loadedEndpoint } from "models/loaded";
import {
  StrategyDashboardInventoryData,
  useGetStrategyDashboardInventoryQuery,
} from "src/store/api/generatedApi";
import {
  GroupedInventorySeries,
  InventoryTimeSeries,
  InventoryTimeSeriesKey,
} from "../types";

export const useStrategyDashboardInventory = (
  start?: string,
  end?: string
): Loaded<GroupedInventorySeries> => {
  const loadedRawData = loadedEndpoint(
    useGetStrategyDashboardInventoryQuery({
      tenantName: useTenant(),
      start,
      end,
    })
  );
  if (loadedRawData.status === "success") {
    return {
      status: "success",
      data: buildGroupedInventorySeries(loadedRawData.data),
      fetching: loadedRawData.fetching,
    };
  }
  return loadedRawData;
};

const buildGroupedInventorySeries = (
  data: StrategyDashboardInventoryData
): GroupedInventorySeries => {
  const stringKeyKeyMap: Record<string, InventoryTimeSeriesKey> = {};
  const stringKeyValuesMap: Record<string, InventoryTimeSeries> = {};

  for (const item of data.series) {
    const key: InventoryTimeSeriesKey = {
      materialGroupingName: item.material_grouping_name,
      materialGroupName: item.material_group_name,
    };
    const stringKey = JSON.stringify(key);
    stringKeyKeyMap[stringKey] = key;

    const value = stringKeyValuesMap[stringKey] ?? {
      date: [],
      inventory_mass: [],
      inventory_fraction: [],
      specific_price: [],
    };
    value.date.push(item.inventory_date);
    value.inventory_mass.push(item.inventory_mass);
    value.inventory_fraction.push(item.inventory_fraction);
    value.specific_price.push(item.specific_price);
    stringKeyValuesMap[stringKey] = value;
  }

  const values: [InventoryTimeSeriesKey, InventoryTimeSeries][] =
    Object.entries(stringKeyKeyMap).map(([stringKey, key]) => [
      key,
      stringKeyValuesMap[stringKey]!,
    ]);

  return {
    values,
    availableMaterialGroupings: data.material_groupings,
  };
};

/**
 * The latest date across the series, as UTC midnight. Only each series' last
 * entry is checked, which relies on the API returning them in date order.
 */
export const latestInventoryDate = (
  inventorySeries: GroupedInventorySeries
): Date | null => {
  const latestDateString = inventorySeries.values
    .map(([, series]) => series.date[series.date.length - 1] ?? null)
    .reduce<string | null>((latest, current) => {
      if (latest === null) {
        return current;
      }
      if (current === null) {
        return latest;
      }
      return current > latest ? current : latest;
    }, null);
  return latestDateString ? new Date(Date.parse(latestDateString)) : null;
};
